/**
 * AssetsTab — grid/list of portal artifacts (Phase 3.3).
 * Categories: All | Documents | Forms | Links | Generated
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Button,
} from '@mui/material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { Plan } from '../../types/generated/graphql';

type AssetCategory = 'all' | 'documents' | 'forms' | 'links' | 'generated';
type ViewMode = 'grid' | 'list';

interface AssetsTabProps {
  plan: Plan;
}

const CATEGORIES: { value: AssetCategory; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'documents', label: 'Documents' },
  { value: 'forms', label: 'Forms' },
  { value: 'links', label: 'Links' },
  { value: 'generated', label: 'Generated' },
];

export const AssetsTab: React.FC<AssetsTabProps> = ({ plan }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [category, setCategory] = useState<AssetCategory>('all');

  // TODO: wire to real assets from plan once schema supports it
  const assets: { id: string; name: string; type: string; stepIds: string[] }[] = [];

  return (
    <Box>
      {/* Upload zone */}
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mb: 3,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 36, color: 'action.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Drag &amp; drop files or
        </Typography>
        <Button size="small" sx={{ mt: 0.5 }}>
          Add asset
        </Button>
      </Paper>

      {/* Toolbar: categories + view mode */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Stack direction="row" gap={0.5}>
          {CATEGORIES.map((c) => (
            <Chip
              key={c.value}
              label={c.label}
              size="small"
              variant={category === c.value ? 'filled' : 'outlined'}
              onClick={() => setCategory(c.value)}
              color={category === c.value ? 'primary' : 'default'}
            />
          ))}
        </Stack>
        <ToggleButtonGroup size="small" value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)}>
          <ToggleButton value="grid"><ViewModuleIcon fontSize="small" /></ToggleButton>
          <ToggleButton value="list"><ViewListIcon fontSize="small" /></ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Assets list / grid */}
      {assets.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <InsertDriveFileIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No assets yet. Upload files or generate them from steps.
          </Typography>
        </Paper>
      ) : (
        <Stack direction={viewMode === 'grid' ? 'row' : 'column'} flexWrap="wrap" gap={2}>
          {assets.map((a) => (
            <Paper key={a.id} variant="outlined" sx={{ p: 2, width: viewMode === 'grid' ? 200 : '100%', borderRadius: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.name}</Typography>
              <Typography variant="caption" color="text.secondary">{a.type}</Typography>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default AssetsTab;
